
import { PropType, computed, defineComponent, ref, nextTick, onMounted } from 'vue'
import first from 'lodash/first'
import TextField from '../../inputs/TextField.vue'
import CustomActivatorSelect from '../../inputs/CustomActivatorSelect.vue'
import BaseInputOutlined from '../../inputs/BaseInputOutlined.vue'
import { useProject } from '../../../hooks/useProject'
import { CHANNEL_TYPES, CONNECTOR_TYPES, PROVIDER_TO_CHANNEL_MAP } from '@root/src/vars/general'
import cfg from '@root/config'
import { watch } from 'vue'
export default defineComponent({
    name: 'SendChannelsStep',
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    components: { TextField, CustomActivatorSelect, BaseInputOutlined },
    setup(props, { emit }) {
        const { project } = useProject()
        const isHrProject = computed(() => {
            return project.value.connectors.find((con) => con.connectorType === CONNECTOR_TYPES.GCB2)?.hrProject || false
        })
        const hasTgHrBranchConnector = computed(() => {
            return project.value.connectors.some(
                connector => connector.connectorType === CONNECTOR_TYPES.telegramHrBranch
            )
        })
        
        // Получаем каналы проекта, если включены каналы доработка
        const projectChannels = computed(() => {
            if (!project.value?.isChannelsRework) return []
            
            const channelTypes = Object.values(CHANNEL_TYPES)
            return project.value.connectors.filter(c => 
                !c.isChisai && 
                channelTypes.includes(c.connectorType)
            )
        })
        
        // Группы каналов для нумерации
        const channelGroups = [
            {
                name: 'whatsapp',
                channelTypes: [CHANNEL_TYPES.whatsappChannel],
            },
            {
                name: 'telegram',
                channelTypes: [CHANNEL_TYPES.telegramChannel],
            },
            {
                name: 'max',
                channelTypes: [CHANNEL_TYPES.maxChannel],
            },
        ]
        
        // Получаем индекс канала в группе
        const getChannelIndex = (connectorType: string, connectorId: string) => {
            if (!project.value || !project.value.connectors) return 1
            
            // Для провайдеров получаем соответствующий тип канала через маппинг
            const channelType = CHANNEL_TYPES[connectorType as keyof typeof CHANNEL_TYPES] 
                || PROVIDER_TO_CHANNEL_MAP[connectorType as keyof typeof PROVIDER_TO_CHANNEL_MAP]
                || connectorType
            
            const group = channelGroups.find(g => g.channelTypes.includes(channelType))
            if (!group) return 1
            
            // Фильтруем только те каналы, которые реально отображаются (projectChannels),
            // и считаем индекс внутри этой группы, чтобы нумерация начиналась с 1
            const sortedChannels = projectChannels.value
                .filter(c => {
                    const cChannelType = CHANNEL_TYPES[c.connectorType as keyof typeof CHANNEL_TYPES] 
                        || PROVIDER_TO_CHANNEL_MAP[c.connectorType as keyof typeof PROVIDER_TO_CHANNEL_MAP]
                        || c.connectorType
                    return cChannelType === channelType
                })
                .sort((a, b) => {
                    const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                    const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) {
                        return aTime - bTime
                    }
                    if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                    return a.connectorId.localeCompare(b.connectorId)
                })
            
            const currentIndex = sortedChannels.findIndex(c => c.connectorId === connectorId)
            return currentIndex >= 0 ? currentIndex + 1 : 1
        }
        
        // Получаем отображаемое имя канала
        const getChannelDisplayName = (connectorType: string, index: number) => {
            // Для провайдеров получаем соответствующий тип канала через маппинг
            const channelType = CHANNEL_TYPES[connectorType as keyof typeof CHANNEL_TYPES] 
                || PROVIDER_TO_CHANNEL_MAP[connectorType as keyof typeof PROVIDER_TO_CHANNEL_MAP]
                || connectorType
            
            const baseNames: Record<string, string> = {
                [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                [CHANNEL_TYPES.maxChannel]: 'MAX',
                [CONNECTOR_TYPES.telegramHr]: 'HR Telegram',
                [CONNECTOR_TYPES.telegramHrBranch]: 'HR Telegram (филиал)',
            }
            const baseName = baseNames[channelType] || baseNames[connectorType] || connectorType
            
            if (index === 1 && connectorType !== CONNECTOR_TYPES.telegramHrBranch) {
                return baseName
            }
            if (connectorType === CONNECTOR_TYPES.telegramHr || connectorType === CONNECTOR_TYPES.telegramHrBranch) {
                return baseName
            }
            return `${baseName} ${index}`
        }
        
        const sendChannelsList = computed(() =>{
            let list = [
                { id: 'whatsappBasis', title: 'whatsapp' },
                { id: 'whatsappDh', title: 'Умный помощник Data Heroes (whatsapp)' },
                { id: 'tgDh', title: 'Умный помощник Data Heroes (telegram)' },
            ]
            if(isHrProject.value) {
                list.push({ id: 'telegramHr', title: 'HR Telegram' },)
            }
            if(hasTgHrBranchConnector.value && [51, 8].includes(props.value.templateId)) {
                list.push({ id: 'telegramHrBranch', title: 'HR Telegram (филиал)' },)
            }
            
            // Если включены каналы доработка, добавляем все каналы проекта
            if (project.value?.isChannelsRework && projectChannels.value.length > 0) {
                list = list.filter(el => el.id !== 'whatsappBasis')
                projectChannels.value.forEach(connector => {
                    const connectorType = connector.connectorType
                    const index = getChannelIndex(connectorType, connector.connectorId)
                    const displayName = getChannelDisplayName(connectorType, index)
                    const connectorId = connector.connectorId
                    const phone = connector.phone
                    const title = phone ? `${displayName} (${phone})` : displayName
                    
                    list.push({
                        id: connectorId,
                        title: title,
                    })
                })
            }
            
            return list
        })
        const update = (val: string) => {
            emit('input', Object.assign({}, props.value, { sendChannels: [val] }))
            nextTick(() => {
                emit('validation-updated', Boolean(props.value.sendChannels?.length))
            })
        }
        onMounted(() => {
            emit('validation-updated', Boolean(props.value.sendChannels.length))
        })
        
        // Отслеживаем изменения sendChannels и обновляем валидацию
        watch(
            () => props.value.sendChannels,
            (newChannels) => {
                nextTick(() => {
                    emit('validation-updated', Boolean(newChannels?.length))
                })
            },
            { deep: true }
        )
        
        const clear = () => {
            nextTick(() => {
                emit('validation-updated', false)
            })
        }
        return { update, clear, sendChannelsList, first }
    },
})
